import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { chartColors, sparseTicks, formatCompact } from './chartTheme';

// Shared tooltip. Recharts' default is a white box with borders that ignores
// the theme; this one uses the app's own surface tokens.
const Box = ({ label, rows }) => (
  <div className="rounded-xl bg-card border border-border px-3 py-2 shadow-lg pointer-events-none">
    {label && <p className="text-[11px] font-bold text-foreground">{label}</p>}
    {rows.map((r) => (
      <p key={r.label} className="text-[11px] text-muted-foreground mt-0.5">
        {r.label}: <span className="font-bold text-foreground tabular-nums">{r.value}</span>
      </p>
    ))}
  </div>
);

/* ── Columns over time ─────────────────────────────────────────────────────
   cursor={false} removes recharts' grey slab behind the hovered column; the
   bar itself brightens instead, which is what the eye is already looking at. */
export const TimeColumns = ({ data, height = 220, valueLabel = 'Value', format = formatCompact }) => {
  const { theme } = useTheme();
  const c = chartColors(theme);
  const [hovered, setHovered] = useState(null);

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 4, left: -20, bottom: 0 }} onMouseLeave={() => setHovered(null)}>
          <CartesianGrid vertical={false} stroke={c.grid} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={{ fontSize: 10, fill: c.axis }}
            tickFormatter={(_, i) => sparseTicks(data, i)}
          />
          <YAxis axisLine={false} tickLine={false} width={46} tick={{ fontSize: 10, fill: c.axis }} tickFormatter={format} />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => (active && payload?.length
              ? <Box label={payload[0].payload.label} rows={[{ label: valueLabel, value: format(payload[0].value) }]} />
              : null)}
          />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false}
               onMouseOver={(_, i) => setHovered(i)}>
            {data.map((d, i) => (
              <Cell
                key={d.date || i}
                fill={d.value ? c.mark : c.muted}
                fillOpacity={hovered === null || hovered === i ? 1 : 0.45}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Cumulative trend ─────────────────────────────────────────────────────
   A single series, so no legend: the card title names it. Area fill at low
   opacity so the line stays the thing you read. */
export const TrendArea = ({ data, height = 220, valueLabel = 'Total', format = formatCompact }) => {
  const { theme } = useTheme();
  const c = chartColors(theme);
  const gradientId = `trend-${valueLabel.replace(/\W/g, '')}`;

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c.mark} stopOpacity={0.22} />
              <stop offset="100%" stopColor={c.mark} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke={c.grid} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={{ fontSize: 10, fill: c.axis }}
            tickFormatter={(_, i) => sparseTicks(data, i)}
          />
          <YAxis axisLine={false} tickLine={false} width={46} tick={{ fontSize: 10, fill: c.axis }} tickFormatter={format} />
          <Tooltip
            cursor={{ stroke: c.axis, strokeWidth: 1, strokeOpacity: 0.35 }}
            content={({ active, payload }) => (active && payload?.length
              ? <Box label={payload[0].payload.label} rows={[{ label: valueLabel, value: format(payload[0].value) }]} />
              : null)}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={c.mark}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: c.surface }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Ranked horizontal bars ───────────────────────────────────────────────
   Nominal categories, so every bar takes slot 1. Colouring them by their own
   value would re-encode what bar length already shows. */
export const RankedBars = ({ data, height, nameKey = 'name', valueKey = 'value', valueLabel = 'Count', format = formatCompact }) => {
  const { theme } = useTheme();
  const c = chartColors(theme);
  const computed = height || Math.max(120, data.length * 32 + 16);

  return (
    <div style={{ height: computed }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 36, left: 4, bottom: 4 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey={nameKey}
            axisLine={false}
            tickLine={false}
            width={96}
            tick={{ fontSize: 11, fill: c.axis }}
          />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => (active && payload?.length
              ? <Box label={payload[0].payload[nameKey]} rows={[{ label: valueLabel, value: format(payload[0].value) }]} />
              : null)}
          />
          <Bar dataKey={valueKey} fill={c.mark} radius={[0, 4, 4, 0]} maxBarSize={16} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ── Donut ────────────────────────────────────────────────────────────────
   Part-to-whole at a glance. Capped at five slices plus "Other" — past that
   adjacent slices blur — with the total in the hole and a labelled legend, so
   identity never rests on colour alone. */
export const Donut = ({ data, height = 220, totalLabel = 'Total', format = formatCompact }) => {
  const { theme } = useTheme();
  const c = chartColors(theme);

  const slices = useMemo(() => {
    const sorted = [...data].sort((a, b) => b.value - a.value);
    if (sorted.length <= 5) return sorted;
    const head = sorted.slice(0, 5);
    const tail = sorted.slice(5).reduce((sum, d) => sum + d.value, 0);
    return tail ? [...head, { name: 'Other', value: tail }] : head;
  }, [data]);

  const total = slices.reduce((sum, d) => sum + d.value, 0);
  if (!total) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-5">
      <div className="relative flex-shrink-0" style={{ width: height, height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius="62%"
              outerRadius="92%"
              paddingAngle={2}
              stroke={c.surface}
              strokeWidth={2}
              isAnimationActive={false}
            >
              {slices.map((d, i) => <Cell key={d.name} fill={c.categorical[i % c.categorical.length]} />)}
            </Pie>
            <Tooltip
              content={({ active, payload }) => (active && payload?.length
                ? <Box
                    label={payload[0].name}
                    rows={[
                      { label: 'Count', value: format(payload[0].value) },
                      { label: 'Share', value: `${Math.round((payload[0].value / total) * 100)}%` },
                    ]}
                  />
                : null)}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-black text-foreground leading-none">{format(total)}</span>
          <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wide mt-1">{totalLabel}</span>
        </div>
      </div>

      <ul className="flex-1 min-w-0 w-full space-y-2 list-none p-0 m-0">
        {slices.map((d, i) => (
          <li key={d.name} className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: c.categorical[i % c.categorical.length] }}
            />
            <span className="text-xs text-foreground font-medium truncate flex-1">{d.name}</span>
            <span className="text-xs text-muted-foreground font-bold tabular-nums">
              {Math.round((d.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ── Scores over time ─────────────────────────────────────────────────────
   Points, not bars: quiz results are moments, not accumulations. */
export const ScoreLine = ({ data, height = 220, valueLabel = 'Score' }) => {
  const { theme } = useTheme();
  const c = chartColors(theme);

  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={c.grid} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            tick={{ fontSize: 10, fill: c.axis }}
          />
          <YAxis domain={[0, 100]} axisLine={false} tickLine={false} width={40} tick={{ fontSize: 10, fill: c.axis }} />
          <Tooltip
            cursor={{ stroke: c.axis, strokeWidth: 1, strokeOpacity: 0.35 }}
            content={({ active, payload }) => (active && payload?.length
              ? <Box label={payload[0].payload.label} rows={[{ label: valueLabel, value: `${payload[0].value}%` }]} />
              : null)}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={c.mark}
            strokeWidth={2}
            dot={{ r: 3, fill: c.mark, strokeWidth: 2, stroke: c.surface }}
            activeDot={{ r: 5, strokeWidth: 2, stroke: c.surface }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
